import { useEffect, useRef, useState } from 'react';

import { motion } from 'motion/react';
import Markdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { AnswerSource } from '@cfa/pathway-fe-shared/xp';

import { cn } from '@/utils/tailwind';
import { uniqueBy } from '@/utils/uniqueBy';

import { ExcerptHighlighter } from './excerpt-highlighter';
import MoreButton from './MoreButton';
import Sources from './Sources';

interface AnimatedResponseProps {
  results: string;
  sources?: AnswerSource[];
}

const MAX_HEIGHT = 100;

const normalizeMarkdown = (text: string): string => {
  const withTables = text
    .replace(/\| \|/g, '|\n|')
    .replace(/([^|\n]) \|/, '$1\n\n|');

  return withTables
    .split('\n')
    .map(line =>
      line.trimStart().startsWith('|')
        ? line
        : line.replace(/<br>/g, '\n').replace(/\s+([.,!?;:])/g, '$1'),
    )
    .join('\n');
};

const SmartResponse: React.FC<AnimatedResponseProps> = ({
  results,
  sources = [],
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldClip, setShouldClip] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const uniqueSources = uniqueBy(sources, 'name');

  // Custom markdown components that highlight excerpt matches in text
  const markdownComponents: Components = {
    p: ({ children }) => (
      <p>
        <ExcerptHighlighter sources={sources.filter(_ => _.cited)}>
          {children}
        </ExcerptHighlighter>
      </p>
    ),
    li: ({ children }) => (
      <li>
        <ExcerptHighlighter sources={sources.filter(_ => _.cited)}>
          {children}
        </ExcerptHighlighter>
      </li>
    ),
  };

  useEffect(() => {
    if (contentRef.current) {
      const contentHeight = contentRef.current.scrollHeight;
      setShouldClip(contentHeight > MAX_HEIGHT);
    }
  }, [results]);

  const normalizedResults = normalizeMarkdown(results);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div
        ref={contentRef}
        className={cn(
          'text-variant-body1 relative [&_ol]:list-inside [&_ol]:list-decimal [&_ul]:list-inside [&_ul]:list-disc',
          '[&_h1]:text-variant-h1 [&_h2]:text-variant-h2 [&_h3]:text-variant-h3 [&_h4]:text-variant-h4',
          '[&_h1]:pt-2 [&_h2]:pt-2 [&_h3]:pt-2 [&_h4]:pt-2',
          '[&_table]:shadow-elevation-1 [&_table]:my-2 [&_table]:w-full [&_table]:border-collapse [&_table]:bg-white',
          '[&_th]:border-grayscale-gray2 [&_th]:bg-base-2 [&_th]:border [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold',
          '[&_td]:border-grayscale-gray2 [&_td]:border [&_td]:px-3 [&_td]:py-2',
          {
            'overflow-hidden': shouldClip && !isExpanded,
          },
        )}
        style={{
          maxHeight: shouldClip && !isExpanded ? `${MAX_HEIGHT}px` : 'none',
          maskImage:
            shouldClip && !isExpanded
              ? 'linear-gradient(to bottom, black calc(100% - 32px), transparent 100%)'
              : 'none',
          WebkitMaskImage:
            shouldClip && !isExpanded
              ? 'linear-gradient(to bottom, black calc(100% - 32px), transparent 100%)'
              : 'none',
        }}
      >
        <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {normalizedResults}
        </Markdown>
        <div className="mt-4 flex flex-wrap gap-2">
          <Sources sources={uniqueSources} />
        </div>
      </div>
      {shouldClip && !isExpanded && (
        <MoreButton onClick={() => setIsExpanded(!isExpanded)} />
      )}
    </motion.div>
  );
};

export default SmartResponse;
